import 'mdn-polyfills/Array.prototype.fill';
import 'mdn-polyfills/Array.prototype.find';
import 'mdn-polyfills/Array.prototype.findIndex';
import 'mdn-polyfills/Array.prototype.includes';
import 'mdn-polyfills/Element.prototype.closest';
import 'mdn-polyfills/HTMLCanvasElement.prototype.toBlob';
import 'mdn-polyfills/String.prototype.endsWith';
import 'mdn-polyfills/String.prototype.startsWith';
import 'mdn-polyfills/String.prototype.includes';

import React from 'react';
import ReactDOM from 'react-dom';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
// @ts-ignore
import { ThroughProvider } from 'react-through';
import { LocalizeProvider } from 'react-localize-redux';
import outdatedBrowserRework from 'outdated-browser-rework';
import Survicate from '@survicate/survicate-web-package/survicate_widget';
import App from '@/containers/App';
import { history, store, persistor } from './store';
import { format } from '@/utils/datetime';
import { getCurrentLanguageCode } from './utils/i18n';
import { initSurvicate } from '@/utils/survicate';
import { appInsights } from './AppInsights';
import { installChunkLoadErrorRecoveryListeners, recoverFromChunkLoadError } from '@/utils/chunkLoadRecovery';

initSurvicate(process.env.REACT_APP_SURVICATE_WORKSPACE_KEY, (name, props) =>
    appInsights.trackEvent({ name }, props)
);
if (process.env.REACT_APP_SURVICATE_WORKSPACE_KEY) {
    // Expose on window only outside production so the SDK can be driven from DevTools
    // (e.g. manually firing events for QA without going through the full UI flow).
    if (process.env.NODE_ENV !== 'production') {
        (window as any).Survicate = Survicate;
    }
}

if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`).catch((error) => {
        console.error(`Service worker registration failed: ${error}`);
    });
} else {
    console.error('Service workers are not supported.');
}

// eslint-disable-next-line no-extend-native
Date.prototype.toISOString = function toLocalISOString() {
    return format(this, "yyyy-MM-dd'T'HH:mm:ss");
};
installChunkLoadErrorRecoveryListeners();

outdatedBrowserRework({
    language: getCurrentLanguageCode,
    isUnknownBrowserOK: true,
});

const rootEl = document.getElementById('root');

const render = (Component: React.ComponentType) =>
    ReactDOM.render(
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <LocalizeProvider store={store}>
                    <ThroughProvider>
                        <ConnectedRouter history={history}>
                            <Component />
                        </ConnectedRouter>
                    </ThroughProvider>
                </LocalizeProvider>
            </PersistGate>
        </Provider>,
        rootEl
    );

render(App);

if (module.hot) {
    module.hot.accept('./containers/App', () => {
        const NextApp = require('./containers/App').default;
        render(NextApp);
    });
}

// Log all unhandled errors to server
window.onerror = function myErrorHandler(message, line, col, error) {
    recoverFromChunkLoadError(error ?? message);
    store.dispatch({
        type: 'LOG_ERRORS_REQUEST',
        payload: {
            ExceptionName: message,
            InnerExceptionName: window.navigator.userAgent,
            ExceptionMessage: message,
            URL: window.location.href,
            StackTrace: error,
        },
    });
    return false;
};
